import React, { FC, memo, useMemo, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { TextField, Checkbox } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { GenericMenuOptionV2 } from 'src/types';

const useStyles = makeStyles({
  root: {
    width: 300
  }
});

interface Props {
  label?: string;
  options: GenericMenuOptionV2[];
  isMultiple?: boolean;
  defaultAll?: boolean;
  onHandleChange: (
    options: GenericMenuOptionV2 | GenericMenuOptionV2[]
  ) => void;
}

const ALL_OPTION: GenericMenuOptionV2 = { id: -1, name: 'ALL', value: 'All' };

const Component: FC<Props> = ({
  label = 'Options',
  isMultiple,
  defaultAll = false,
  options,
  onHandleChange
}) => {
  // eslint-disable-next-line no-unused-vars
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState<
    GenericMenuOptionV2[] | GenericMenuOptionV2 | null
  >(isMultiple ? [] : null);

  useEffect(() => {
    if (
      isMultiple &&
      defaultAll &&
      Array.isArray(value) &&
      value.length === 0 &&
      options.length > 0
    ) {
      setValue(options);
      onHandleChange(options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiple, defaultAll, options]);

  // only add "All" when multiple
  const displayOptions = isMultiple ? [ALL_OPTION, ...options] : options;

  const onHandleSelectedOptionChange = (
    newValue: GenericMenuOptionV2 | GenericMenuOptionV2[]
  ) => {
    if (!newValue) return;

    if (Array.isArray(newValue)) {
      // check if All was clicked
      const isAllSelected = newValue.some((o) => o.id === ALL_OPTION.id);

      let finalValue: GenericMenuOptionV2[];
      if (isAllSelected) {
        // if already all selected → unselect all
        const currentlyAllSelected =
          Array.isArray(value) && value.length === options.length;
        finalValue = currentlyAllSelected ? [] : options;
      } else {
        finalValue = newValue;
      }

      setValue(finalValue);
      onHandleChange(finalValue);
    } else {
      // single mode
      setValue(newValue);
      onHandleChange(newValue);
    }
  };

  // just checked all options
  const isAllChecked = useMemo(
    () =>
      isMultiple &&
      Array.isArray(value) &&
      value.length > 0 &&
      value.length === options.length,
    [isMultiple, options, value]
  );

  return (
    <Autocomplete
      style={{ background: 'white' }}
      id="options-dropdown"
      options={displayOptions || []}
      value={value}
      limitTags={2}
      fullWidth
      multiple={isMultiple}
      disableCloseOnSelect
      onChange={(event, newValue) =>
        onHandleSelectedOptionChange(newValue || [])
      }
      open={open}
      getOptionLabel={(option) => option.value}
      renderOption={(option, { selected }) => {
        const checked = option.id === ALL_OPTION.id ? isAllChecked : selected;
        return (
          <>
            {isMultiple && (
              <Checkbox checked={checked} style={{ marginRight: 8 }} />
            )}
            {option.value}
          </>
        );
      }}
      renderTags={(value: GenericMenuOptionV2[]) =>
        !focused ? (
          <div
            style={{
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '1.5em'
            }}
          >
            {isAllChecked
              ? 'All'
              : value
                  .slice()
                  .reverse()
                  .map((v) => v.value)
                  .join(', ')}
          </div>
        ) : null
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={focused ? `Search ${label}` : ''}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            style: { flexWrap: 'nowrap' },
            inputProps: {
              ...params.inputProps,
              style: {
                caretColor: focused ? 'black' : 'transparent'
              }
            }
          }}
          onBlur={() => {
            setOpen(false);
            setFocused(false);
          }}
          onClick={() => {
            setOpen(!open);
            setFocused(!focused);
          }}
        />
      )}
    />
  );
};

export const MultipleSelectedDropdown = memo(Component);
